import React, { useCallback, useEffect, useState } from "react";
import { Loader2, TriangleAlert, Truck, Warehouse, MapPin, Wrench, PackagePlus,
         Undo2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";

const errText = (e, fallback) => {
  const d = e?.response?.data?.detail;
  return typeof d === "string" ? d : d?.message || fallback;
};

const CARDS = [
  { key: "waiting", label: "Short", hint: "Not on hand, not ordered or delivered short",
    icon: TriangleAlert, tone: "text-red-700", bg: "bg-red-50 border-red-200" },
  { key: "staged", label: "Staged in the shop", hint: "Pulled and waiting on a truck",
    icon: Warehouse, tone: "text-amber-800", bg: "bg-amber-50 border-amber-200" },
  { key: "on_site", label: "On site", hint: "Delivered and not yet installed",
    icon: MapPin, tone: "text-emerald-700", bg: "bg-emerald-50 border-emerald-200" },
  { key: "installed", label: "Installed", hint: "Derived from the completion check-off",
    icon: Wrench, tone: "text-blue-700", bg: "bg-blue-50 border-blue-200" },
  { key: "surplus", label: "Surplus", hint: "More on site than the FRS now asks for",
    icon: PackagePlus, tone: "text-amber-900", bg: "bg-amber-50 border-amber-200" },
  { key: "returns_pending", label: "Going back", hint: "Returned, awaiting the shop's receipt",
    icon: Undo2, tone: "text-gray-700", bg: "bg-gray-50 border-gray-200" },
];

const Bar = ({ row }) => {
  const total = Math.max(row.demand, row.installed + row.on_site + row.staged + row.waiting, 1);
  const seg = [["installed", "#1d4ed8"], ["on_site", "#059669"], ["staged", "#d97706"],
               ["waiting", "#dc2626"]];
  return (
    <span className="flex h-2 w-28 rounded overflow-hidden bg-gray-200">
      {seg.map(([k, c]) => (
        row[k] > 0 ? <span key={k} title={`${k.replace("_", " ")}: ${row[k]}`}
                           style={{ width: `${(row[k] / total) * 100}%`, background: c }} /> : null
      ))}
    </span>
  );
};

const Rows = ({ title, rows, label, testid }) => (
  <div className="border border-gray-200 rounded-lg bg-white overflow-x-auto">
    <div className="px-3 py-1.5 text-[10px] uppercase tracking-wide text-gray-500 border-b border-gray-100">
      {title}
    </div>
    <table className="w-full text-[11px]" data-testid={testid}>
      <thead className="text-[10px] uppercase text-gray-500">
        <tr>
          <th className="text-left p-2">{label}</th>
          <th className="text-right p-2">Demand</th>
          <th className="text-right p-2">Short</th>
          <th className="text-right p-2">Staged</th>
          <th className="text-right p-2">On site</th>
          <th className="text-right p-2">Installed</th>
          <th className="p-2"></th>
        </tr>
      </thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={i} className="border-t border-gray-100"
              data-testid={`${testid}-row-${r.name || `${r.building}-${r.floor}`}`}>
            <td className="p-2 font-medium">
              {r.name || `${r.building}${r.building && r.floor ? " · " : ""}${r.floor}` || "—"}
            </td>
            <td className="p-2 text-right tabular-nums">{r.demand}</td>
            <td className={`p-2 text-right tabular-nums ${r.waiting > 0 ? "text-red-700 font-semibold" : "text-gray-400"}`}>
              {r.waiting || ""}
            </td>
            <td className={`p-2 text-right tabular-nums ${r.staged > 0 ? "text-amber-800 font-semibold" : "text-gray-400"}`}>
              {r.staged || ""}
            </td>
            <td className={`p-2 text-right tabular-nums ${r.on_site > 0 ? "text-emerald-700 font-semibold" : "text-gray-400"}`}>
              {r.on_site || ""}
            </td>
            <td className="p-2 text-right tabular-nums text-blue-700">{r.installed || ""}</td>
            <td className="p-2"><Bar row={r} /></td>
          </tr>
        ))}
      </tbody>
    </table>
  </div>
);

/** FLD-6 — what is short, what is staged, what is on site. One screen. */
export default function MaterialDashboard({ projectId, onGoTo }) {
  const [data, setData] = useState(null);

  const load = useCallback(async () => {
    try {
      const { data: d } = await api.get("/material/dashboard",
        { params: { project_id: projectId } });
      setData(d);
    } catch (e) { toast.error(errText(e, "Could not read the material status")); }
  }, [projectId]);
  useEffect(() => { load(); }, [load]);

  if (!data) {
    return <div className="p-6 text-center"><Loader2 className="w-4 h-4 animate-spin inline" /></div>;
  }
  const t = data.totals;

  return (
    <div className="space-y-3" data-testid="material-dashboard">
      {data.dispatch_warnings.length > 0 && (
        <div className="border border-red-300 bg-red-50 rounded-lg p-2 space-y-1"
             data-testid="dispatch-warnings">
          <div className="text-[10px] uppercase tracking-wide font-semibold text-red-800 flex items-center gap-1">
            <TriangleAlert className="w-3.5 h-3.5" /> Crews going out without their material
          </div>
          {data.dispatch_warnings.map((w) => (
            <div key={w.mobilization_id} className="text-[11px] text-red-900"
                 data-testid={`dispatch-warning-${w.mobilization_id}`}>
              <b>{w.title}</b> starts {w.start}
              {w.days_out >= 0 ? ` (in ${w.days_out}d)` : " (already started)"} —{" "}
              <b className="tabular-nums">{w.uncovered}</b> unit(s) for {w.rooms} room(s) are not on
              site: {w.products.join(", ")}
            </div>
          ))}
        </div>
      )}

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2"
           data-testid="material-cards">
        {CARDS.map((c) => {
          const Icon = c.icon;
          const val = c.key === "returns_pending" ? t.returns_pending : t[c.key];
          return (
            <div key={c.key} title={c.hint}
                 data-testid={`material-card-${c.key}`}
                 className={`border rounded-lg p-2 ${val > 0 ? c.bg : "bg-white border-gray-200"}`}>
              <div className="flex items-center gap-1 text-[10px] uppercase tracking-wide text-gray-500">
                <Icon className={`w-3 h-3 ${val > 0 ? c.tone : "text-gray-400"}`} /> {c.label}
              </div>
              <div className={`text-2xl font-bold tabular-nums ${val > 0 ? c.tone : "text-gray-300"}`}>
                {val}
              </div>
            </div>
          );
        })}
      </div>

      <div className="flex flex-wrap items-center gap-2 text-[11px] text-gray-600">
        <span><b className="text-gray-900 tabular-nums">{t.demand}</b> units of demand on the FRS</span>
        {Object.entries(data.tickets).map(([status, n]) => (
          <button key={status} onClick={() => onGoTo?.("tickets")}
                  data-testid={`ticket-count-${status.replace(/ /g, "-")}`}
                  className="px-1.5 py-0.5 rounded border border-gray-300 bg-white hover:bg-gray-50">
            <Truck className="w-3 h-3 inline mr-0.5 text-gray-500" />
            {n} {status.toLowerCase()}
          </button>
        ))}
        {t.waiting > 0 && (
          <button onClick={() => onGoTo?.("waiting")} data-testid="dashboard-go-waiting"
                  className="ml-auto text-red-700 underline underline-offset-2">
            See what is being chased
          </button>
        )}
      </div>

      <Rows title="By scope" rows={data.by_scope} label="Scope" testid="material-by-scope" />
      <Rows title="By floor" rows={data.by_floor} label="Building / floor"
            testid="material-by-floor" />
    </div>
  );
}
